import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    SliderContainer,
    SliderHeader,
    SliderLabel,
    SliderValue,
    StyledInput,
    SliderWrapper,
    SliderTrack,
    SliderFill,
    SliderInput,
    SliderRange,
    TooltipContainer,
    TooltipTitle,
    TooltipRow,
    TooltipValue
} from 'components/ClientHome/CalculatorStyle';
import { formatCurrency } from 'components/ClientHome/CalculatorLogic';

export const RangeSlider = ({
    value,
    onChange,
    min,
    max,
    step,
    prefix = '',
    suffix = '',
    label,
    findClosestStepValue = null
}) => {
    const [inputValue, setInputValue] = useState(value);
    const percentage = ((value - min) / (max - min)) * 100;

    const handleInputChange = (e) => {
        setInputValue(e.target.value.replace(/[^0-9.]/g, ''));
    };

    const handleBlur = () => {
        let newValue = parseFloat(inputValue);

        if (isNaN(newValue)) {
            setInputValue(value);
            return;
        }

        if (newValue < min) newValue = min;
        if (newValue > max) newValue = max;

        if (suffix.includes('Years')) {
            newValue = Math.round(newValue);
        } else if (suffix === '%') {
            newValue = Math.round(newValue * 2) / 2;
        } else if (prefix === '₹') {
            newValue = Math.round(newValue);
        }

        setInputValue(newValue);
        onChange(newValue);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    };

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    return (
        <SliderContainer>
            <SliderHeader>
                <SliderLabel>{label}</SliderLabel>
                <SliderValueInput
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    prefix={prefix}
                    suffix={suffix}
                />
            </SliderHeader>

            <SliderWrapper>
                <SliderTrack />
                <SliderFill percentage={percentage} />
                <SliderInput
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={(e) => {
                        const val = Number(e.target.value);
                        if (prefix === '₹' && label.includes("Investment") && findClosestStepValue) {
                            onChange(findClosestStepValue(val));
                        } else {
                            onChange(val);
                        }
                    }}
                />
            </SliderWrapper>

            <SliderRange>
                <span>{prefix}{min.toLocaleString('en-IN')}{suffix}</span>
                <span>{prefix}{max.toLocaleString('en-IN')}{suffix}</span>
            </SliderRange>
        </SliderContainer>
    );
};

const SliderValueInput = ({ value, onChange, onBlur, onKeyDown, prefix, suffix }) => {
    const inputRef = useRef(null);
    
    const resizeInput = useCallback(() => {
        if (!inputRef.current) return;
        
        const span = document.createElement('span');
        span.style.visibility = 'hidden';
        span.style.position = 'absolute';
        span.style.whiteSpace = 'pre';
        span.style.font = window.getComputedStyle(inputRef.current).font;
        span.textContent = inputRef.current.value || '0';
        
        document.body.appendChild(span);
        const width = span.getBoundingClientRect().width;
        document.body.removeChild(span);
        
        const paddingWidth = 8;
        const minWidth = 40;
        inputRef.current.style.width = `${Math.max(width + paddingWidth, minWidth)}px`;
    }, []);
    
    useEffect(() => {
        resizeInput();
    }, [value, resizeInput]);
    
    return (
        <SliderValue>
            <span>{prefix}</span>
            <StyledInput
                ref={inputRef}
                type="text"
                value={value}
                onChange={(e) => {
                    onChange(e);
                    requestAnimationFrame(resizeInput);
                }}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
            />
            <span>{suffix}</span>
        </SliderValue>
    );
};

export const ChartHoverInfo = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const investment = payload[0].value;
    const returns = payload[1].value;
    const total = investment + returns;

    return (
        <TooltipContainer>
            <TooltipTitle>Year {label}</TooltipTitle>
            <TooltipRow color="#967DCC">
                Investment <TooltipValue>{formatCurrency(investment)}</TooltipValue>
            </TooltipRow>
            <TooltipRow color="#936FE0">
                Returns <TooltipValue>{formatCurrency(returns)}</TooltipValue>
            </TooltipRow>
            <TooltipRow color="#111827" style={{ marginTop: 8, fontWeight: 600 }}>
                Total <TooltipValue>{formatCurrency(total)}</TooltipValue>
            </TooltipRow>
        </TooltipContainer>
    );
};

export const ChartLabel = (props) => {
    const { payload } = props;
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            paddingTop: '15px'
        }}>
            {payload.map((entry, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                        width: '3rem',
                        height: '0.5rem',
                        backgroundColor: entry.color,
                        borderRadius: '6px'
                    }} />
                    <span style={{
                        marginLeft: '1rem',
                        fontSize: '1.6rem',
                        fontFamily: 'Lato',
                        color: '#7E7E7E'
                    }}>
                        {entry.value}
                    </span>
                </div>
            ))}
        </div>
    );
};